/**
 * The console's lookback picker — one row above everything it scopes (design
 * rules §4.3), state in the URL (`?lookback=`) so the board's slow section, the
 * investigate page and a shared link all agree on the window.
 *
 * It is not the board-kit `FilterRow`: that row speaks in calendar days
 * (today / 7 / 30 / 90) and custom `from`/`to` windows, which the backend
 * cannot honour here — `FoSlowInteractions(lookback)` is served for exactly
 * five trailing windows ending now. Offering a date picker that changed nothing
 * would be a control that lies.
 */

import { Chip, ChipGroup, Typography } from "@norican/react";

import type { FoConsoleLookback } from "@/lib/api";
import { useDrillParams } from "@/lib/drill";

import { LOOKBACKS, parseLookback } from "./console-format";
import { LOOKBACK_PARAM } from "./params";

/**
 * The lookback the URL carries, defaulting to 24 h.
 *
 * `fallback` exists for the one screen whose honest default is different — the
 * pinpoint board ranks a cumulative cost and its route is served for a week —
 * and must be passed to `LookbackRow` as well, or the chips would highlight a
 * window the data was not built for.
 */
export function useLookbackParam(fallback?: FoConsoleLookback): FoConsoleLookback {
  const drill = useDrillParams(LOOKBACK_PARAM);
  return parseLookback(drill.params[LOOKBACK_PARAM], fallback);
}

interface LookbackRowProps {
  /** What the row scopes, stated beside the chips — "this section", "this person's view". */
  scopes: string;
  /** The window a URL with no `?lookback=` means. Defaults to 24 h. */
  fallback?: FoConsoleLookback;
}

export function LookbackRow({ scopes, fallback }: LookbackRowProps) {
  const drill = useDrillParams(LOOKBACK_PARAM);
  const lookback = parseLookback(drill.params[LOOKBACK_PARAM], fallback);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <ChipGroup.Root
        aria-label="Lookback window"
        value={[lookback]}
        onValueChange={(value) => {
          const next = value[0];
          if (next) {
            drill.set({ [LOOKBACK_PARAM]: next });
          }
        }}
      >
        {LOOKBACKS.map((option) => (
          <Chip key={option.id} value={option.id} size="sm">
            {option.label}
          </Chip>
        ))}
      </ChipGroup.Root>
      <Typography variant="caption" tone="muted">
        Lookback scopes {scopes}; the other panels keep their own fixed windows.
      </Typography>
    </div>
  );
}
